import {ColorsType, StyleType} from '../../../../utils/theming';
import {AvatarSize, AvatarType} from './Avatar.types.web';

/*
 * ---------------
 * Colors:
 * The color, state, and style props will define the background, border, and text colors applied.
 * ----------------
 */
export const createColorMap = (Colors: ColorsType) => ({
  white: {
    enable: {
      backgroundColor: Colors.neutral100,
      iconColor: Colors.neutral1000,
    },
    hover: {
      backgroundColor: Colors.neutral300,
      iconColor: Colors.neutral1000,
    },
    disable: {
      backgroundColor: Colors.neutral400,
      iconColor: Colors.neutral500,
    },
  },
  black: {
    enable: {
      backgroundColor: Colors.neutral1000,
      iconColor: Colors.neutral100,
    },
    hover: {
      backgroundColor: Colors.neutral800,
      iconColor: Colors.neutral100,
    },
    disable: {
      backgroundColor: Colors.neutral400,
      iconColor: Colors.neutral500,
    },
  },
  purple: {
    enable: {
      backgroundColor: Colors.primary700,
      iconColor: Colors.neutral100,
    },
    hover: {
      backgroundColor: Colors.primary900,
      iconColor: Colors.neutral100,
    },
    disable: {
      backgroundColor: Colors.neutral400,
      iconColor: Colors.neutral500,
    },
  },
  lightPurple: {
    enable: {
      backgroundColor: Colors.primary100,
      iconColor: Colors.primary700,
    },
    hover: {
      backgroundColor: Colors.primary300,
      iconColor: Colors.primary700,
    },
    disable: {
      backgroundColor: Colors.neutral400,
      iconColor: Colors.neutral500,
    },
  },
  red: {
    enable: {
      backgroundColor: Colors.alert300,
      iconColor: Colors.neutral100,
    },
    hover: {
      backgroundColor: Colors.alert400,
      iconColor: Colors.neutral100,
    },
    disable: {
      backgroundColor: Colors.neutral400,
      iconColor: Colors.neutral500,
    },
  },
  green: {
    enable: {
      backgroundColor: Colors.success300,
      iconColor: Colors.neutral100,
    },
    hover: {
      backgroundColor: Colors.success400,
      iconColor: Colors.neutral100,
    },
    disable: {
      backgroundColor: Colors.neutral400,
      iconColor: Colors.neutral500,
    },
  },
  yellow: {
    enable: {
      backgroundColor: Colors.warning300,
      iconColor: Colors.neutral1000,
    },
    hover: {
      backgroundColor: Colors.warning400,
      iconColor: Colors.neutral1000,
    },
    disable: {
      backgroundColor: Colors.neutral400,
      iconColor: Colors.neutral500,
    },
  },
  blue: {
    enable: {
      backgroundColor: Colors.info300,
      iconColor: Colors.neutral100,
    },
    hover: {
      backgroundColor: Colors.info400,
      iconColor: Colors.neutral100,
    },
    disable: {
      backgroundColor: Colors.neutral400,
      iconColor: Colors.neutral500,
    },
  },
  teal: {
    enable: {
      backgroundColor: Colors.secondary600,
      iconColor: Colors.neutral100,
    },
    hover: {
      backgroundColor: Colors.secondary800,
      iconColor: Colors.neutral100,
    },
    disable: {
      backgroundColor: Colors.neutral400,
      iconColor: Colors.neutral500,
    },
  },
  lightTeal: {
    enable: {
      backgroundColor: Colors.secondary200,
      iconColor: Colors.secondary600,
    },
    hover: {
      backgroundColor: Colors.secondary400,
      iconColor: Colors.secondary600,
    },
    disable: {
      backgroundColor: Colors.neutral400,
      iconColor: Colors.neutral500,
    },
  },
  grey: {
    enable: {
      backgroundColor: Colors.neutral500,
      iconColor: Colors.neutral100,
    },
    hover: {
      backgroundColor: Colors.neutral300,
      iconColor: Colors.neutral1000,
    },
    disable: {
      backgroundColor: Colors.neutral400,
      iconColor: Colors.neutral500,
    },
  },
  pink: {
    enable: {
      backgroundColor: Colors.tertiary600,
      iconColor: Colors.neutral100,
    },
    hover: {
      backgroundColor: Colors.tertiary800,
      iconColor: Colors.neutral100,
    },
    disable: {
      backgroundColor: Colors.neutral400,
      iconColor: Colors.neutral500,
    },
  },
  lightPink: {
    enable: {
      backgroundColor: Colors.tertiary200,
      iconColor: Colors.tertiary600,
    },
    hover: {
      backgroundColor: Colors.tertiary400,
      iconColor: Colors.tertiary600,
    },
    disable: {
      backgroundColor: Colors.neutral400,
      iconColor: Colors.neutral500,
    },
  },
});

export const avatarBgColorMap = (Colors: ColorsType) => ({
  black: Colors.neutral1000,
  purple: Colors.primary400,
  pink: Colors.tertiary500,
  teal: Colors.secondary600,
  gray: Colors.neutral600,
});

export const avatarContainerSizeMap: Record<string, any> = {
  '2xl': 120,
  large: 68,
  medium: 44,
  small: 36,
};

export const avatarSizeMap: Record<string, any> = {
  '2xl': 112,
  large: 64,
  medium: 40,
  small: 32,
};

export const avatarBorderRadiousMap: Record<string, any> = {
  '2xl': 173.583,
  large: 99.19,
  medium: 61.994,
  small: 49.595,
};

export const avatarFontsMap = (fonts: StyleType) => ({
  '2xl': fonts['heading2'],
  large: fonts['heading6'],
  medium: fonts['bodyBoldSM'],
  small: fonts['bodyBoldXS'],
});

/* Avatar indicatorSize */
export const avatarIndicatorSizeMap: Record<string, string> = {
  '2xl': '28',
  large: '16',
  medium: '10',
  small: '8',
};

/* Avatar indicatorSize */
export const avatarButtonIconSizeMap: Record<string, string> = {
  '2xl': '2xlarge',
  large: 'medium',
  medium: 'xsmall',
  small: '2xsmall',
};

/* Avatar container styles */
export const getAvatarGeneralStyles = (
  colors: ColorsType,
  size: string,
  color: string,
  type?: AvatarType,
  image?: string,
) => {
  const bgColorMap = avatarBgColorMap(colors);

  return {
    backgroundColor: !(type === 'image' && image?.length)
      ? // @ts-ignore
        bgColorMap[color]
      : 'transparent',
    backgroundImage:
      type === 'image' && image?.length
        ? `url(${image})`
        : // @ts-ignore
          bgColorMap[color] || 'transparent',
    borderRadius: avatarBorderRadiousMap[size],
    height: avatarSizeMap[size],
    width: avatarSizeMap[size],
  };
};

/* Avatar paddings */
export const getAvatarPaddings = (size: string) => {
  return size === 'small' ? '8px' : '12px';
};

export const getAvatarMinSize = (size: string) => {
  return size === 'small' ? 32 : size === 'medium' ? 40 : 48;
};

export const getAvatarTextStyles = (
  fontStyles: StyleType,
  colors: ColorsType,
  size: string,
) => {
  // @ts-ignore
  const buttonGeneralFontStyle = avatarFontsMap(fontStyles)[size];

  const buttonFontStyle = {
    ...buttonGeneralFontStyle,
    lineHeight: buttonGeneralFontStyle?.lineHeight + 'px',
    color: colors.neutral100,
    margin: 0,
    textAlign: 'center',
  };

  return {...buttonFontStyle};
};

export function getInitials(text: string) {
  const words = text
    .split(' ')
    .filter(p => p)
    .slice(0, 2);
  const initials = words.map(p => p[0].toUpperCase());
  return initials.join('');
}
